/**
 * Quick-create patient from the appointment form.
 * Cancel asks to discard; X and Esc close immediately; backdrop does not close.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  Alert,
  Button,
  CharacterCount,
  DatePicker,
  FormControl,
  FormDialog,
  FormHelperText,
  InputLabel,
  MenuItem,
  PhoneTextField,
  Select,
  SelectPlaceholder,
  Stack,
  TextField,
} from '@/shared/ui';
import { PATIENT_NAME_MAX_LENGTH } from '@/shared/constants/personFields';
import { getApiErrorDetails } from '@/shared/utils/apiError';
import { phoneValidation } from '@/shared/utils/validation';
import { clinicDisplayName } from '@/shared/clinic';
import { patientAPI } from '@/modules/patient/api/patient.api';
import type { Patient } from '@/shared/types/patient';
import {
  EMPTY_PATIENT_FORM_VALUES,
  buildCreatePatientPayload,
} from '@/modules/patient/utils/patientForm';
import type { AddPatientLocationState } from '@/modules/patient/utils/patientForm';

interface ClinicOption {
  _id: string;
  name: string;
}

type DuplicatePatient = {
  id: string;
  message: string;
};

type QuickPatientFormValues = {
  firstName: string;
  phone: string;
  gender: string;
  dateOfBirth: Date | null;
  clinicId: string;
};

export type QuickCreatePatientDialogProps = {
  open: boolean;
  initialFirstName?: string;
  defaultClinicId?: string;
  clinics: ClinicOption[];
  onClose: () => void;
  onCreated: (patient: Patient) => void;
  onSelectExisting: (patient: Patient) => void;
};

const selectChromeSx = {
  '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
  '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
} as const;

const QuickCreatePatientDialog: React.FC<QuickCreatePatientDialogProps> = ({
  open,
  initialFirstName = '',
  defaultClinicId = '',
  clinics,
  onClose,
  onCreated,
  onSelectExisting,
}) => {
  const { t } = useTranslation(['appointment', 'patient', 'common']);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicatePatient | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectingExisting, setSelectingExisting] = useState(false);

  const schema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string().trim().required(t('patient:validation.firstNameRequired')),
        phone: phoneValidation,
        gender: Yup.string()
          .oneOf(['male', 'female', 'other'], t('patient:validation.genderRequired'))
          .required(t('patient:validation.genderRequired')),
        dateOfBirth: Yup.date().nullable().defined(),
        clinicId: Yup.string().required(t('patient:validation.clinicRequired')),
      }),
    [t]
  );

  const emptyValues = (): QuickPatientFormValues => ({
    firstName: initialFirstName.trim(),
    phone: '',
    gender: '',
    dateOfBirth: null,
    clinicId: defaultClinicId || clinics[0]?._id || '',
  });

  const form = useForm<QuickPatientFormValues>({
    defaultValues: emptyValues(),
    resolver: yupResolver(schema) as unknown as Resolver<QuickPatientFormValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { control, handleSubmit, reset, getValues } = form;

  useEffect(() => {
    if (!open) return;
    reset(emptyValues());
    setError(null);
    setDuplicate(null);
    setSubmitting(false);
    setSelectingExisting(false);
    // Reset only when the dialog opens or its seed changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emptyValues closes over the seed props
  }, [open, initialFirstName, defaultClinicId, clinics, reset]);

  const buildPrefillState = (): AddPatientLocationState => {
    const values = getValues();
    return {
      prefill: {
        firstName: values.firstName.trim() || undefined,
        phone: values.phone.trim() || undefined,
        gender: values.gender || undefined,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : undefined,
        clinicId: values.clinicId || undefined,
      },
    };
  };

  const handleOpenFullForm = (): void => {
    onClose();
    navigate('/patients', {
      state: { openAddPatient: true, prefill: buildPrefillState().prefill },
    });
  };

  const handleSelectExisting = async (): Promise<void> => {
    if (!duplicate) return;
    setSelectingExisting(true);
    setError(null);
    try {
      const res = await patientAPI.getPatient(duplicate.id);
      const patient = res.data;
      if (!patient?._id) {
        setError(t('appointment:dialogs.selectExistingFailed'));
        return;
      }
      onSelectExisting(patient);
      onClose();
    } catch (err) {
      setError(
        getApiErrorDetails(err, t('appointment:dialogs.selectExistingFailed')).message
      );
    } finally {
      setSelectingExisting(false);
    }
  };

  const save = async (values: QuickPatientFormValues): Promise<void> => {
    setError(null);
    setDuplicate(null);
    setSubmitting(true);
    try {
      const payload = buildCreatePatientPayload({
        ...EMPTY_PATIENT_FORM_VALUES,
        firstName: values.firstName.trim(),
        phone: values.phone.trim(),
        gender: values.gender as 'male' | 'female' | 'other',
        dateOfBirth: values.dateOfBirth,
        clinicId: values.clinicId,
      });
      const res = await patientAPI.createPatient(payload);
      const created = res.data;
      if (!created?._id) {
        setError(t('patient:toasts.addFailedRetry'));
        return;
      }
      onCreated(created);
      onClose();
    } catch (err) {
      const details = getApiErrorDetails(err, t('patient:toasts.addFailedRetry'));
      const existingId = details.data?.existingPatientId;
      if (
        details.code === 'PATIENT_PHONE_EXISTS' &&
        typeof existingId === 'string' &&
        existingId
      ) {
        setDuplicate({
          id: existingId,
          message: t('appointment:dialogs.phoneExistsPatient'),
        });
        setError(null);
      } else {
        setError(details.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || selectingExisting;

  return (
    <FormDialog
      open={open}
      title={t('appointment:dialogs.quickCreatePatientTitle')}
      busy={busy}
      onClose={onClose}
      onSubmit={() => void handleSubmit((values) => void save(values))()}
      submitLabel={t('common:save')}
      busySubmitLabel={t('common:saving')}
      startSlot={
        <Button intent="secondary" onClick={handleOpenFullForm} disabled={busy}>
          {t('appointment:dialogs.openFullForm')}
        </Button>
      }
    >
      <FormProvider {...form}>
        <Stack spacing={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label={t('patient:fields.firstName')}
                placeholder={t('patient:placeholders.firstName')}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                required
                fullWidth
                autoFocus
                disabled={busy}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                labelEnd={
                  <CharacterCount current={field.value.length} max={PATIENT_NAME_MAX_LENGTH} />
                }
                slotProps={{ htmlInput: { maxLength: PATIENT_NAME_MAX_LENGTH } }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <PhoneTextField
                label={t('patient:fields.phone')}
                placeholder={t('patient:placeholders.phone')}
                value={field.value}
                onChange={(phone) => {
                  field.onChange(phone);
                  setDuplicate(null);
                }}
                onBlur={field.onBlur}
                inputRef={field.ref}
                fullWidth
                disabled={busy}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="gender"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth required disabled={busy} error={Boolean(fieldState.error)}>
                <InputLabel id="quick-patient-gender-label" shrink>
                  {t('patient:fields.gender')}
                </InputLabel>
                <Select
                  labelId="quick-patient-gender-label"
                  id="quick-patient-gender"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  label={t('patient:fields.gender')}
                  displayEmpty
                  notched
                  sx={selectChromeSx}
                  renderValue={(value) =>
                    value === 'male' || value === 'female' || value === 'other' ? (
                      t(`patient:gender.${value}`)
                    ) : (
                      <SelectPlaceholder>{t('patient:placeholders.gender')}</SelectPlaceholder>
                    )
                  }
                >
                  <MenuItem value="">{t('patient:placeholders.gender')}</MenuItem>
                  <MenuItem value="male">{t('patient:gender.male')}</MenuItem>
                  <MenuItem value="female">{t('patient:gender.female')}</MenuItem>
                  <MenuItem value="other">{t('patient:gender.other')}</MenuItem>
                </Select>
                {fieldState.error ? (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field, fieldState }) => (
              <DatePicker
                label={t('patient:fields.dateOfBirth')}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                disabled={busy}
                disableFuture
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                slotProps={{
                  textField: {
                    name: field.name,
                    onBlur: field.onBlur,
                    inputRef: field.ref,
                    placeholder: t('patient:placeholders.dateOfBirth'),
                  },
                }}
              />
            )}
          />
          <Controller
            name="clinicId"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth required disabled={busy} error={Boolean(fieldState.error)}>
                <InputLabel id="quick-patient-clinic-label" shrink>
                  {t('patient:fields.clinic')}
                </InputLabel>
                <Select
                  labelId="quick-patient-clinic-label"
                  id="quick-patient-clinic"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  label={t('patient:fields.clinic')}
                  displayEmpty
                  notched
                  sx={selectChromeSx}
                  renderValue={(value) => {
                    const clinic = clinics.find((item) => item._id === value);
                    return clinic ? (
                      clinicDisplayName(clinic)
                    ) : (
                      <SelectPlaceholder>{t('patient:placeholders.clinic')}</SelectPlaceholder>
                    );
                  }}
                >
                  <MenuItem value="">{t('patient:placeholders.clinic')}</MenuItem>
                  {clinics.map((clinic) => (
                    <MenuItem key={clinic._id} value={clinic._id}>
                      {clinicDisplayName(clinic)}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error ? (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
          {duplicate ? (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void handleSelectExisting()}
                  disabled={busy}
                >
                  {t('appointment:dialogs.selectExistingPatient')}
                </Button>
              }
            >
              {duplicate.message}
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </FormProvider>
    </FormDialog>
  );
};

export default QuickCreatePatientDialog;
